import { useState } from 'react';
import { HelpCircle, ExternalLink, X } from 'lucide-react';
import { Button } from '../ui/button';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { useNavigate } from 'react-router-dom';
import { getTooltip, isEnglishOnlyPortal } from '../../data/tooltipData';

/**
 * HelpTooltip Component
 * 
 * Renders a contextual help icon with tooltip that:
 * - Shows brief, actionable help text
 * - Provides "Learn More" link to Documentation Hub
 * - Respects language policy (English-only for Retailer/Admin/Charity)
 * 
 * @param {string} portal - 'retailer' | 'consumer' | 'admin' | 'charity'
 * @param {string} category - Tooltip category (e.g., 'scanner', 'orders', 'cart')
 * @param {string} tooltipKey - Specific tooltip key (e.g., 'main', 'acknowledge')
 * @param {function} t - Translation function (optional, for Consumer portal)
 * @param {string} size - Icon size: 'sm' | 'md' | 'lg'
 * @param {string} className - Additional CSS classes
 */
export function HelpTooltip({ 
  portal = 'consumer', 
  category, 
  tooltipKey = 'main',
  t = null,
  size = 'sm',
  className = ''
}) {
  const navigate = useNavigate();
  const tooltip = getTooltip(portal, category, tooltipKey);
  
  if (!tooltip) return null;
  
  const isEnglishOnly = isEnglishOnlyPortal(portal);
  
  // Get title and content (handle i18n for Consumer)
  const title = !isEnglishOnly && t && tooltip.titleKey 
    ? t(tooltip.titleKey, tooltip.titleFallback)
    : tooltip.title || tooltip.titleFallback;
    
  const content = !isEnglishOnly && t && tooltip.contentKey
    ? t(tooltip.contentKey, tooltip.contentFallback)
    : tooltip.content || tooltip.contentFallback;

  const sizeClasses = {
    sm: 'h-3.5 w-3.5',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  const handleLearnMore = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (tooltip.docLink) {
      navigate(tooltip.docLink);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button 
            className={`inline-flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors ${className}`}
            data-testid={`help-tooltip-${tooltip.id}`}
            aria-label={`Help: ${title}`}
          >
            <HelpCircle className={sizeClasses[size]} />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs p-3 bg-white border shadow-lg"
          sideOffset={5}
        >
          <div className="space-y-2">
            <p className="font-medium text-gray-900 text-sm">{title}</p>
            <p className="text-gray-600 text-xs leading-relaxed">{content}</p>
            {tooltip.docLink && (
              <button
                onClick={handleLearnMore}
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium mt-1"
              >
                Learn more
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * InlineHelpTip Component
 * 
 * A more prominent inline help component for important features.
 * Shows as a small card that can be dismissed.
 */
export function InlineHelpTip({
  portal = 'consumer',
  category,
  tooltipKey = 'main',
  t = null,
  dismissible = true,
  storageKey = null,
  className = ''
}) {
  const [dismissed, setDismissed] = useState(() => {
    if (storageKey) {
      return localStorage.getItem(`help-dismissed-${storageKey}`) === 'true';
    }
    return false;
  });
  
  const navigate = useNavigate();
  const tooltip = getTooltip(portal, category, tooltipKey);
  
  if (!tooltip || dismissed) return null;
  
  const isEnglishOnly = isEnglishOnlyPortal(portal);
  
  const title = !isEnglishOnly && t && tooltip.titleKey 
    ? t(tooltip.titleKey, tooltip.titleFallback)
    : tooltip.title || tooltip.titleFallback;
    
  const content = !isEnglishOnly && t && tooltip.contentKey
    ? t(tooltip.contentKey, tooltip.contentFallback)
    : tooltip.content || tooltip.contentFallback;

  const handleDismiss = () => {
    setDismissed(true);
    if (storageKey) {
      localStorage.setItem(`help-dismissed-${storageKey}`, 'true');
    }
  };

  const handleLearnMore = () => {
    if (tooltip.docLink) {
      navigate(tooltip.docLink);
    }
  };

  return (
    <div 
      className={`bg-blue-50 border border-blue-200 rounded-lg p-3 ${className}`}
      data-testid={`inline-help-${tooltip.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <HelpCircle className="h-5 w-5 text-blue-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-blue-900">{title}</p>
          <p className="text-xs text-blue-700 mt-1">{content}</p>
          {tooltip.docLink && (
            <button
              onClick={handleLearnMore}
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium mt-2"
            >
              Learn more
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-blue-400 hover:text-blue-600"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * SectionHelpHeader Component
 * 
 * A section header with integrated help tooltip.
 */
export function SectionHelpHeader({
  title,
  portal = 'consumer',
  category,
  tooltipKey = 'main',
  t = null,
  className = '',
  children
}) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <HelpTooltip 
        portal={portal} 
        category={category} 
        tooltipKey={tooltipKey}
        t={t}
        size="sm"
      />
      {children}
    </div>
  );
}

export default HelpTooltip;
